import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Check, 
  Crown, 
  Zap, 
  AlertCircle,
  CreditCard,
  Loader2
} from 'lucide-react';
import { 
  useSubscriptionPlans, 
  useUserSubscriptionStatus,
  useSubscribe,
  useUpgradeSubscription,
  useCancelSubscription,
  useReactivateSubscription
} from '@/hooks/useSubscriptions';
import type { SubscriptionPlan } from '@/types/billing';
import { usePaymentMethods, useBillingUsage } from '@/hooks/useBilling';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function SubscriptionPlansTab() {
  const { user } = useAuth();
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isPreparingSubscription, setIsPreparingSubscription] = useState(false);

  const { data: plans, isLoading: plansLoading, error: plansError } = useSubscriptionPlans(user?.id);
  const { data: subscriptionStatus, isLoading: statusLoading } = useUserSubscriptionStatus();
  const { data: paymentMethods } = usePaymentMethods();
  const { data: billingUsage } = useBillingUsage();
  
  const subscribeMutation = useSubscribe();
  const upgradeMutation = useUpgradeSubscription();
  const cancelMutation = useCancelSubscription();
  const reactivateMutation = useReactivateSubscription();

  const hasPaymentMethod = paymentMethods?.savedMethods && paymentMethods.savedMethods.length > 0;
  const defaultPaymentMethod = paymentMethods?.savedMethods?.find(m => m.is_default);

  // Get current plan details
  const userCurrentPlan = plans?.find(plan => plan.is_current_plan);

  // Helper function to check if a plan requires payment
  const requiresPayment = (plan: SubscriptionPlan): boolean => {
    return plan.price_monthly > 0 || plan.price_one_time > 0;
  };

  // Function to determine if a plan change is an upgrade or downgrade
  const isUpgrade = (targetPlan: SubscriptionPlan) => {
    if (!userCurrentPlan) return true; // Treat as upgrade if no current plan

    // 1) Primary: Compare recurring monthly price
    if (targetPlan.price_monthly !== userCurrentPlan.price_monthly) {
      return targetPlan.price_monthly > userCurrentPlan.price_monthly;
    }

    // 2) Secondary: Consider immediate charge (one-time fee)
    // If monthly prices are equal, a higher one-time fee should be treated as an upgrade
    if (targetPlan.price_one_time !== userCurrentPlan.price_one_time) {
      return targetPlan.price_one_time > userCurrentPlan.price_one_time;
    }

    // 3) Tertiary: Compare included value (monthly credits)
    if (targetPlan.monthly_credits !== userCurrentPlan.monthly_credits) {
      return targetPlan.monthly_credits > userCurrentPlan.monthly_credits;
    }

    // 4) Final tiebreaker: display order (lower = higher tier)
    return targetPlan.display_order < userCurrentPlan.display_order;
  };

  const handleSubscribe = async (planIdentifier: string) => {
    if (!hasPaymentMethod) {
      toast.error('Please add a payment method first');
      return;
    }

    // Provide immediate feedback
    setIsPreparingSubscription(true);
    toast.loading('Preparing subscription...', { id: 'subscription' });

    try {
      await new Promise(resolve => setTimeout(resolve, 600)); // Brief delay for UX
      
      subscribeMutation.mutate({
        planIdentifier,
        paymentMethodId: defaultPaymentMethod?.stripe_payment_method_id,
      }, {
        onSuccess: () => {
          toast.dismiss('subscription');
          setIsPreparingSubscription(false);
        },
        onError: () => {
          toast.dismiss('subscription');
          setIsPreparingSubscription(false);
        }
      });
    } catch {
      toast.dismiss('subscription');
      setIsPreparingSubscription(false);
    }
  };

  const handleUpgrade = async (planIdentifier: string) => {
    upgradeMutation.mutate({
      planIdentifier,
      paymentMethodId: defaultPaymentMethod?.stripe_payment_method_id,
    });
    setShowUpgradeConfirm(null);
  };

  const handleCancel = async () => {
    cancelMutation.mutate({ cancelAtPeriodEnd: true });
    setShowCancelConfirm(false);
  };

  const handleReactivate = async () => {
    reactivateMutation.mutate();
  };

  if (plansLoading || statusLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20 mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3].map((j) => (
                    <Skeleton key={j} className="h-4 w-full" />
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <Skeleton className="h-10 w-full" />
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load subscription plans. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const currentPlan = plans?.find(plan => plan.is_current_plan);

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      {subscriptionStatus && (
        <div className="mb-6 space-y-4">
          {/* Free Trial Alert */}
          {billingUsage?.freeTrialInfo?.isInFreeTrial && (
            <Alert className={
              (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3
                ? "border-destructive/20 bg-destructive/5"
                : "border-primary/20 bg-primary/5"
            }>
              <Zap className={`h-4 w-4 ${
                (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3
                  ? "text-destructive"
                  : "text-primary"
              }`} />
              <AlertDescription className={
                (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3
                  ? "text-destructive"
                  : "text-primary"
              }>
                {billingUsage.freeTrialInfo.trialType === 'stripe' ? (
                  <>
                    <strong>Trial Active</strong>
                    {billingUsage.freeTrialInfo.daysRemaining !== undefined && (
                      <span> - Your plan will activate automatically in <strong>{billingUsage.freeTrialInfo.daysRemaining} days</strong>.</span>
                    )}
                  </>
                ) : (
                  <>
                    <strong>
                      {(billingUsage.freeTrialInfo.daysRemaining || 0) <= 3 
                        ? "Free Credits Ending Soon!" 
                        : "Free Credits Active"}
                    </strong>
                    {billingUsage.freeTrialInfo.daysRemaining !== undefined && (
                      <span> Your free credits expire in <strong>{billingUsage.freeTrialInfo.daysRemaining} days</strong>. Choose a plan to continue service.</span>
                    )}
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {subscriptionStatus.needs_stripe_subscription && currentPlan && (
            <Alert className="border-destructive/20 bg-destructive/5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You're assigned to the <strong>{currentPlan.name}</strong> but need to set up payment to activate your subscription.
              </AlertDescription>
            </Alert>
          )}
          
          {currentPlan && !subscriptionStatus.needs_stripe_subscription && !billingUsage?.freeTrialInfo?.isInFreeTrial && (
            <Alert className={subscriptionStatus.cancel_at_period_end
              ? "border-destructive/20 bg-destructive/5"
              : "border-success/20 bg-success/5"
            }>
              {subscriptionStatus.cancel_at_period_end ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <Check className="h-4 w-4 text-success" />
              )}
              <AlertDescription className={subscriptionStatus.cancel_at_period_end ? "" : "text-success"}>
                You're currently subscribed to the <strong>{currentPlan.name}</strong>.
                {subscriptionStatus.cancel_at_period_end ? (
                  <span> Your subscription will cancel at the end of your billing period ({new Date(subscriptionStatus.current_period_end!).toLocaleDateString()}).</span>
                ) : subscriptionStatus.stripe_subscription_status === 'active' ? (
                  <span> Your subscription is active and ready to use.</span>
                ) : null}
              </AlertDescription>
            </Alert>
          )}

          {/* Cancelled Trial Alert */}
          {billingUsage?.freeTrialInfo?.isInFreeTrial && subscriptionStatus.cancel_at_period_end && (
            <Alert className="border-destructive/20 bg-destructive/5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Trial Cancelled</strong> - Your trial will end on {new Date(subscriptionStatus.current_period_end!).toLocaleDateString()}. 
                {billingUsage.freeTrialInfo.trialType === 'stripe' ? (
                  <span> You won't be charged and your service will stop.</span>
                ) : (
                  <span> Your free credits will expire and service will stop.</span>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Available Plans */}
      <div>
        <div className="flex flex-col items-center mb-6">
          <h3 className="text-lg font-semibold mb-2">
            {plansLoading 
              ? 'Loading Plans...' 
              : plansError 
              ? 'Error Loading Plans'
              : 'Subscription Plans'
            }
          </h3>
          {!plansLoading && !plansError && plans && (
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {userCurrentPlan ? (
                <>You're currently on the <strong>{userCurrentPlan.name}</strong>. {plans.length > 1 ? 'Upgrade or downgrade anytime.' : ''}</>
              ) : (
                <>Choose the plan that best fits your business needs.</>
              )}
            </p>
          )}
        </div>
        
        {/* Loading State */}
        {plansLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-96">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-1/2 mb-4" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </CardContent>
                <CardFooter>
                  <Skeleton className="h-10 w-full" />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : plansError ? (
          /* Error State */
          <div className="max-w-md mx-auto">
            <Alert className="border-destructive/20 bg-destructive/5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="mb-4">
                Failed to load subscription plans. Please try refreshing the page or contact support if the issue persists.
              </AlertDescription>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Try Again
              </Button>
            </Alert>
          </div>
        ) : (
          <div className={`grid gap-6 ${
            plans && plans.length === 1 
              ? 'grid-cols-1 max-w-md mx-auto' 
              : plans && plans.length === 2
              ? 'md:grid-cols-2 max-w-4xl mx-auto'
              : 'md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {plans?.filter((plan) => {
              // Always show the current plan
              if (plan.is_current_plan) {
                return true;
              }
              
              // Whitelist for test plan - only show to specific emails
              if (plan.plan_identifier === '1-dollar-co-branded-test') {
                const allowedEmails = [
                  'prtnr_co@domain.com',
                  'white-label@gmail.com',
                  // Add more test emails here as needed
                ];
                return allowedEmails.includes(user?.email || '');
              }
              // Show all other plans normally
              return true;
            }).map((plan) => (
            <Card key={plan.id} className={`relative transition-all duration-200 hover:shadow-lg ${
              plan.is_current_plan 
                ? 'ring-2 ring-primary bg-primary/5 shadow-lg' 
                : 'hover:shadow-md'
            }`}>
              {plan.is_current_plan && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground shadow-md">
                    <Crown className="w-3 h-3 mr-1" />
                    Current Plan
                  </Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  {plan.name}
                  {plan.needs_stripe_subscription && requiresPayment(plan) && (
                    <Badge variant="destructive">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Setup Required
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="mb-4">
                  {plan.price_one_time > 0 && plan.price_monthly > 0 ? (
                    // Both one-time and monthly fees
                    <div className="space-y-1">
                      <div className="text-2xl font-bold text-foreground">
                        ${plan.price_one_time}
                      </div>
                      <div className="text-sm text-muted-foreground">one-time setup</div>
                      <div className="text-lg font-semibold text-primary">
                        + ${plan.price_monthly}/month
                      </div>
                    </div>
                  ) : plan.price_one_time > 0 ? (
                    // One-time fee only (lifetime plans)
                    <div className="space-y-1">
                      <div className="text-3xl font-bold text-primary">
                        ${plan.price_one_time}
                      </div>
                      <div className="text-sm text-muted-foreground">one-time payment</div>
                      <div className="text-xs text-success font-medium">Lifetime access • No monthly fees</div>
                    </div>
                  ) : plan.price_monthly > 0 ? (
                    // Monthly fee only
                    <div className="space-y-1">
                      <div className="text-3xl font-bold text-foreground">
                        ${plan.price_monthly}
                      </div>
                      <div className="text-sm text-muted-foreground">per month</div>
                    </div>
                  ) : (
                    // Free plan
                    <div className="space-y-1">
                      <div className="text-3xl font-bold text-success">
                        Free
                      </div>
                      <div className="text-sm text-success font-medium">no payment required</div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm">
                    <Zap className="w-4 h-4 mr-2 text-primary" />
                    {plan.monthly_credits.toLocaleString()} monthly credits
                  </div>
                  {plan.one_time_bonus_credits > 0 && (
                    <div className="flex items-center text-sm">
                      <Check className="w-4 h-4 mr-2 text-success" />
                      {plan.one_time_bonus_credits.toLocaleString()} bonus credits
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  {Array.isArray(plan.features) && plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start text-sm">
                      <Check className="w-4 h-4 mr-2 text-success mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
              
              <CardFooter>
                {plan.is_current_plan ? (
                  <div className="w-full space-y-2">
                    {plan.needs_stripe_subscription && requiresPayment(plan) ? (
                      <Button 
                        onClick={() => handleSubscribe(plan.plan_identifier)}
                        disabled={!hasPaymentMethod || subscribeMutation.isPending || isPreparingSubscription}
                        className="w-full"
                      >
                        {subscribeMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : isPreparingSubscription ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Preparing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Activate Subscription
                          </>
                        )}
                      </Button>
                    ) : plan.needs_stripe_subscription && !requiresPayment(plan) ? (
                      <div className="w-full p-3 bg-success/10 border border-success/20 rounded-md text-center">
                        <Check className="w-5 h-5 mx-auto mb-2 text-success" />
                        <p className="text-sm font-medium text-success">Plan Active</p>
                        <p className="text-xs text-success">No payment required</p>
                      </div>
                    ) : subscriptionStatus?.cancel_at_period_end ? (
                      <div className="w-full space-y-2">
                        <Button 
                          onClick={handleReactivate}
                          disabled={reactivateMutation.isPending}
                          className="w-full"
                        >
                          {reactivateMutation.isPending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4 mr-2" />
                          )}
                          Reactivate Subscription
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          {billingUsage?.freeTrialInfo?.isInFreeTrial ? (
                            <>Trial cancelled - ends on {subscriptionStatus.current_period_end ? new Date(subscriptionStatus.current_period_end).toLocaleDateString() : 'trial end'}</>
                          ) : (
                            <>Currently set to cancel on {subscriptionStatus.current_period_end ? new Date(subscriptionStatus.current_period_end).toLocaleDateString() : 'the billing period end'}</>
                          )}
                        </p>
                      </div>
                    ) : requiresPayment(plan) ? (
                      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="w-full">
                            Cancel Subscription
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to cancel your subscription? It will remain active until the end of your current billing period.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={handleCancel}
                              disabled={cancelMutation.isPending}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {cancelMutation.isPending ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : null}
                              Cancel Subscription
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}
                  </div>
                ) : (
                  <div className="w-full">
                    {!subscriptionStatus?.has_plan_assignment ? (
                      plan.one_time_checkout_url ? (
                        <Button asChild className="w-full">
                          <a 
                            href={plan.one_time_checkout_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label={`Buy ${plan.name} now`}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Buy {plan.name}
                          </a>
                        </Button>
                      ) : requiresPayment(plan) ? (
                        <Button 
                          onClick={() => handleSubscribe(plan.plan_identifier)}
                          disabled={!hasPaymentMethod || subscribeMutation.isPending || isPreparingSubscription}
                          className="w-full"
                        >
                          {subscribeMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : isPreparingSubscription ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Preparing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Subscribe
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleSubscribe(plan.plan_identifier)}
                          disabled={subscribeMutation.isPending || isPreparingSubscription}
                          className="w-full"
                          variant="outline"
                        >
                          {subscribeMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : isPreparingSubscription ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Preparing...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Get Started
                            </>
                          )}
                        </Button>
                      )
                    ) : (
                      plan.one_time_checkout_url ? (
                        <Button 
                          asChild
                          className="w-full"
                        >
                          <a 
                            href={plan.one_time_checkout_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label={`Buy ${plan.name} now`}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Buy {plan.name}
                          </a>
                        </Button>
                      ) : requiresPayment(plan) ? (
                        <AlertDialog 
                          open={showUpgradeConfirm === plan.plan_identifier} 
                          onOpenChange={(open) => setShowUpgradeConfirm(open ? plan.plan_identifier : null)}
                        >
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant={isUpgrade(plan) ? "default" : "destructive"}
                              className="w-full"
                            >
                              {isUpgrade(plan) ? (
                                <>
                                  <Zap className="w-4 h-4 mr-2" />
                                  Upgrade to {plan.name}
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-4 h-4 mr-2" />
                                  Downgrade to {plan.name}
                                </>
                              )}
                            </Button>
                          </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{isUpgrade(plan) ? 'Upgrade' : 'Downgrade'} Subscription</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to {isUpgrade(plan) ? 'upgrade' : 'downgrade'} to {plan.name}? 
                              {isUpgrade(plan) 
                                ? " You'll be charged a prorated amount for the upgrade immediately."
                                : " You'll receive a prorated credit for the downgrade."
                              }
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleUpgrade(plan.plan_identifier)}
                              disabled={!hasPaymentMethod || upgradeMutation.isPending}
                              className={isUpgrade(plan) ? "" : "bg-destructive hover:bg-destructive/90"}
                            >
                              {upgradeMutation.isPending ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : isUpgrade(plan) ? (
                                <Zap className="w-4 h-4 mr-2" />
                              ) : (
                                <AlertCircle className="w-4 h-4 mr-2" />
                              )}
                              {isUpgrade(plan) ? 'Upgrade Now' : 'Downgrade Now'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      ) : (
                        <Button 
                          onClick={() => handleUpgrade(plan.plan_identifier)}
                          disabled={upgradeMutation.isPending}
                          variant="outline"
                          className="w-full"
                        >
                          {upgradeMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Switching...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Switch to {plan.name}
                            </>
                          )}
                        </Button>
                      )
                    )}
                    
                    {!hasPaymentMethod && requiresPayment(plan) && !plan.one_time_checkout_url && (
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        Add a payment method first
                      </p>
                    )}
                  </div>
                )}
              </CardFooter>
            </Card>
          ))}
          
          {/* Empty State */}
          {plans && plans.length === 0 && (
            <div className="col-span-full">
              <Card className="p-8 text-center border-dashed">
                <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                  <CreditCard className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Plans Available</h3>
                <p className="text-muted-foreground mb-4">
                  There are currently no subscription plans available for your account. 
                  Please contact support if you believe this is an error.
                </p>
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Refresh Plans
                </Button>
              </Card>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
} 